<template>
  <div class="pa-6">
    <!-- Breadcrumb and back button -->
    <v-row class="align-center bread-row">
      <v-col cols="12" md="10">
        <Breadcrump :crumbs="crumbs" />
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-end">
        <v-btn @click="$router.go(-1)" fab small dark color="#1976d2">
          <v-icon dark>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Date filter -->
    <v-row class="align-center calendar-row">
      <v-col cols="12" md="3">
        <div class="calendar-wrapper">
          <p class="calendar-label">از:</p>
          <date-picker
            simple
            editable
            v-model="from"
            placeholder="انتخاب تاریخ"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="calendar-wrapper">
          <p class="calendar-label">تا:</p>
          <date-picker
            simple
            editable
            v-model="to"
            placeholder="انتخاب تاریخ"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3" class="d-flex align-center">
        <v-btn
          :loading="loading"
          color="#1976d2"
          class="mt-md-6 mt-2 white--text"
          @click="loadData"
        >
          مشاهده
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" grow color="#1976d2" class="mb-4 elevation-2">
      <v-tab><span class="IranSans">تاریخچه وضعیت</span></v-tab>
      <v-tab v-if="logPerm"><span class="IranSans">لاگ‌های دستگاه</span></v-tab>
    </v-tabs>

    <!-- Tab Content -->
    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Status -->
      <v-tab-item>
        <v-card class="pa-4 mb-6 chart-card elevation-3">
          <v-card-title class="title-font">نمودار وضعیت دستگاه</v-card-title>
          <div class="chart-container">
            <canvas id="device-status-chart"></canvas>
          </div>
        </v-card>

        <v-card class="pa-4 elevation-3">
          <v-card-title class="title-font">سوابق وضعیت دستگاه</v-card-title>
          <v-data-table
            :headers="headers"
            :items="deviceHistoryTable"
            dense
            class="elevation-1"
            :items-per-page="10"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'mdi-arrow-collapse-left',
              lastIcon: 'mdi-arrow-collapse-right',
              prevIcon: 'mdi-arrow-left',
              nextIcon: 'mdi-arrow-right',
              itemsPerPageOptions: [5, 10, 25, 50]
            }"
            no-data-text="داده‌ای برای نمایش وجود ندارد"
          >
            <template v-slot:item.changeTime="{ item }">
              {{ formatJalaliDate(item.changeTime) }}
            </template>
            <template v-slot:item.isOnline="{ item }">
              <v-chip :color="item.isOnline ? 'green' : 'red'" dark small>
                {{ item.isOnline ? "آنلاین" : "آفلاین" }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Logs -->
      <v-tab-item v-if="logPerm">
        <v-card class="pa-4 mb-6 chart-card elevation-3">
          <v-card-title
            class="d-flex justify-space-between align-center title-font"
          >
            نمودار لاگ‌های دستگاه
            <v-btn
              small
              color="primary"
              @click="resetLogSelection"
              :disabled="!selectedLogField"
            >
              ریست
            </v-btn>
          </v-card-title>

          <v-select
            v-if="plottableFields.length > 0"
            v-model="selectedLogField"
            :items="plottableFields"
            item-text="text"
            item-value="value"
            placeholder="فیلد برای نمودار"
            outlined
            dense
            class="mt-3 field-select"
            clearable
            @click:clear="selectedLogField = null"
          ></v-select>

          <div class="chart-container">
            <canvas id="device-logs-chart"></canvas>
          </div>
        </v-card>

        <v-card class="pa-4 elevation-3">
          <v-card-title class="title-font">سوابق لاگ دستگاه</v-card-title>
          <v-data-table
            :headers="logHeaders"
            :items="deviceLogs"
            dense
            class="elevation-1"
            :items-per-page="10"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'mdi-arrow-collapse-left',
              lastIcon: 'mdi-arrow-collapse-right',
              prevIcon: 'mdi-arrow-left',
              nextIcon: 'mdi-arrow-right',
              itemsPerPageOptions: [5, 10, 25, 50]
            }"
            no-data-text="داده‌ای برای نمایش وجود ندارد"
          >
            <template v-slot:item.createdAt="{ item }">
              {{ formatJalaliDate(item.createdAt) }}
            </template>
            <template
              v-for="field in dynamicLogKeys"
              v-slot:[`item.${field}`]="{ item }"
            >
              <v-chip
                v-if="isBooleanField(field)"
                :key="field"
                :color="getBooleanChipColor(item[field], field)"
                dark
                small
              >
                {{ formatBoolean(item[field], field) }}
              </v-chip>
              <span v-else :key="`item.numeric.${field}`">
                <v-chip
                  v-if="hasChipField(field)"
                  :key="field"
                  :color="getNumericChipColor(item[field], field)"
                  dark
                  small
                >
                  {{ formatNumericField(item[field], field) }}
                </v-chip>
                <span v-else>
                  {{ formatNumericField(item[field], field) }}
                </span>
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import Chart from "chart.js";
import moment from "moment-jalaali";
import request from "@/utils/request";
import Breadcrump from "@/components/common/Breadcrump.vue";
import { hasPermission } from "@/composables/permissions";

export default {
  name: "DeviceTimeline",
  components: { Breadcrump },
  data() {
    const today = moment().format("jYYYY/jMM/jDD");
    const fiveDaysAgo = moment()
      .subtract(5, "days")
      .format("jYYYY/jMM/jDD");
    return {
      activeTab: 0,
      crumbs: [
        { text: "خانه", href: "/", disabled: false },
        { text: "مجموعه‌ها", href: "/complexes", disabled: false },
        {
          text: this.$store.state.selectedDeviceName || "دستگاه",
          href: `/complex-devices`,
          disabled: false
        },
        { text: "تاریخچه وضعیت", disabled: true }
      ],
      from: fiveDaysAgo,
      to: today,
      deviceHistory: [],
      deviceHistoryTable: [],
      deviceLogs: [],
      complexId: null,
      chart: null,
      logsChart: null,
      headers: [
        { text: "زمان تغییر", value: "changeTime" },
        { text: "وضعیت", value: "isOnline" }
      ],
      loading: false,
      plottableFields: [],
      selectedLogField: null,
      dynamicLogKeys: [],
      booleanFieldMap: {
        caseDoorFlag: { true: "باز", false: "بسته" },
        shutDownFlag: { true: "خاموش", false: "روشن" },
        externalPowerIsConnected: { true: "متصل", false: "قطع" }
      },
      numericFieldMap: {
        databaseHealth: { 1: "سالم", 2: "خطا", 0: "نامشخص" },
        lightBoxNumber: { 2: "چپ", 3: "راست", 1: "قدیمی" },
        lightBoxStatus: v => v,
        totalMemory: v => (v != null ? `${v} MB` : "-"),
        freeMemory: v => (v != null ? `${v} MB` : "-")
      }
    };
  },
  computed: {
    logPerm() {
      return hasPermission("DeviceLog_Show");
    },
    logHeaders() {
      return [
        { text: "زمان", value: "createdAt" },
        ...this.dynamicLogKeys.map(key => ({
          text: this.formatFieldName(key),
          value: key
        }))
      ];
    }
  },
  watch: {
    selectedLogField() {
      if (this.activeTab === 1 && this.deviceLogs.length > 0) {
        this.$nextTick(() => this.renderLogsChart());
      }
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      if (this.activeTab === 0) {
        this.loadDeviceHistory();
        this.renderChart();
      } else if (this.activeTab === 1) {
        this.loadDeviceLogs();
        this.renderChart();
      }
    },
    async loadDeviceHistory() {
      this.loading = true;
      const deviceId = this.$route.params.deviceId;

      try {
        const gregorianFrom = moment(this.from, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const gregorianTo = moment(this.to, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const response = await request.get(
          `/DeviceHistory/device/${deviceId}`,
          {
            params: { from: gregorianFrom, to: gregorianTo },
            headers: { accept: "application/json" }
          }
        );

        this.deviceHistory = Array.isArray(response.data.result?.deviceHistory)
          ? response.data.result.deviceHistory
          : [];

        if (this.deviceHistory.length > 0) {
          const lastNode = this.deviceHistory[this.deviceHistory.length - 1]; // چون بر اساس زمان نزولی مرتب شده
          const lastTime = new Date(lastNode.changeTime);
          const now = new Date();
          const toDate = new Date(this.to); // فرضاً this.to = "2025-11-15T12:34:56"
          const dateOnly = new Date(
            toDate.getFullYear(),
            toDate.getMonth(),
            toDate.getDate()
          );

          // اضافه کردن یک روز
          dateOnly.setDate(dateOnly.getDate() + 1);

          const diffMinutes = (now - lastTime) / 60000;

          if (now < dateOnly && diffMinutes >= 1) {
            this.deviceHistory.push({
              changeTime: now.toISOString(),
              isOnline: lastNode.isOnline
            });
          }
        }

        this.deviceHistoryTable = [...this.deviceHistory].sort(
          (a, b) => new Date(b.changeTime) - new Date(a.changeTime)
        );

        this.complexId = response.data.result?.complexId ?? null;
        this.crumbs[2].href = this.complexId
          ? `/complex-devices/${this.complexId}`
          : "/complex-devices";

        if (this.activeTab === 0) this.renderChart();
      } catch (error) {
        this.deviceHistory = [];
        this.deviceHistoryTable = [];
        this.$notify({
          title: "خطا",
          text: "دریافت سوابق دستگاه با خطا مواجه شد",
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    },

    async loadDeviceLogs() {
      const deviceId = this.$route.params.deviceId;
      try {
        const gregorianFrom = moment(this.from, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const gregorianTo = moment(this.to, "jYYYY/jMM/jDD").format(
          "YYYY-MM-DD"
        );
        const response = await request.get(`/DeviceLog/device/${deviceId}`, {
          params: { from: gregorianFrom, to: gregorianTo },
          headers: { accept: "application/json" }
        });

        const logs = Array.isArray(response.data.result?.deviceLog)
          ? response.data.result.deviceLog
          : [];

        this.deviceLogs = logs.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        this.extractDynamicFields(logs);

        if (this.activeTab === 1) {
          this.$nextTick(() => this.renderLogsChart());
        }
      } catch (error) {
        this.deviceLogs = [];
        this.plottableFields = [];
        this.dynamicLogKeys = [];
        this.$notify({
          title: "خطا",
          text: "دریافت لاگ‌ها با خطا مواجه شد",
          type: "error"
        });
      }
    },

    extractDynamicFields(logs) {
      if (!logs.length) {
        this.dynamicLogKeys = [];
        this.plottableFields = [];
        return;
      }
      const sample = logs[0];
      const keys = Object.keys(sample).filter(
        k => !["id", "deviceId", "createdAt"].includes(k)
      );
      this.dynamicLogKeys = keys;
      this.plottableFields = keys
        .map(key => {
          const values = logs
            .map(log => log[key])
            .filter(v => v !== "" && v !== undefined);
          if (values.length === 0) return null;
          const allNumbers = values.every(
            v => !isNaN(parseFloat(v)) && isFinite(v)
          );
          const allBooleans = values.every(
            v => v === true || v === false || v === null
          );
          if (allBooleans && this.booleanFieldMap[key] !== undefined) {
            return {
              value: key,
              text: this.formatFieldName(key),
              type: "boolean"
            };
          }
          if (allNumbers) {
            return {
              value: key,
              text: this.formatFieldName(key),
              type: "number"
            };
          }
          return null;
        })
        .filter(Boolean);
    },

    formatFieldName(key) {
      const map = {
        caseDoorFlag: "درب کیس",
        shutDownFlag: "وضعیت خاموشی",
        lightBoxNumber: "موقعیت لایت باکس",
        lightBoxStatus: "لایت باکس",
        externalPowerIsConnected: "برق خارجی",
        totalMemory: "حافظه کل (MB)",
        freeMemory: "حافظه آزاد (MB)",
        screenStatus: "وضعیت اسکرین",
        databaseHealth: "وضعیت دیتابیس"
      };
      return map[key] || key;
    },

    isBooleanField(key) {
      return this.booleanFieldMap[key] !== undefined;
    },

    formatBoolean(value, key) {
      return this.booleanFieldMap[key]?.[value] ?? value;
    },
    hasChipField(key) {
      const colors = {
        databaseHealth: "وضعیت دیتابیس",
        lightBoxNumber: "موقعیت لایت باکس"
      };
      return colors[key] !== undefined;
    },
    getNumericChipColor(value, key) {
      const colors = {
        databaseHealth: { 1: "green", 2: "red" },
        lightBoxNumber: { 1: "green", 2: "orange" }
      };
      return colors[key]?.[value] || "blue";
    },

    getBooleanChipColor(value, key) {
      const colors = {
        caseDoorFlag: { true: "orange", false: "grey" },
        shutDownFlag: { true: "red", false: "green" },
        externalPowerIsConnected: { true: "green", false: "red" }
      };
      return colors[key]?.[value] || "blue";
    },

    formatNumericField(value, key) {
      if (value == null || value === "") return "-";
      if (this.numericFieldMap[key]) {
        const mapper = this.numericFieldMap[key];
        return typeof mapper === "function"
          ? mapper(value)
          : mapper[value] ?? value;
      }
      return value;
    },

    // دقیقاً همون کد اصلی شما — بدون تغییر
    renderChart() {
      const ctx = document.getElementById("device-status-chart");
      if (this.chart) this.chart.destroy();
      if (!this.deviceHistory.length) return;

      const dataPoints = this.deviceHistory.map(x => ({
        x: moment(x.changeTime).format("jYYYY/jMM/jDD HH:mm:ss"),
        y: x.isOnline ? 1 : 0
      }));

      this.chart = new Chart(ctx, {
        type: "line",
        data: {
          datasets: [
            {
              label: "وضعیت دستگاه",
              data: dataPoints,
              borderColor: "#2196f3",
              backgroundColor: "rgba(33,150,243,0.1)",
              fill: true,
              tension: 0,
              steppedLine: true,
              pointRadius: 3
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                type: "time",
                time: {
                  unit: "day",
                  tooltipFormat: "YYYY/MM/DD HH:mm:ss",
                  displayFormats: { hour: "HH:mm", day: "YYYY/MM/DD" }
                },
                scaleLabel: { display: true, labelString: "زمان" }
              }
            ],
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  stepSize: 1,
                  callback: v => (v === 1 ? "آنلاین" : "آفلاین")
                },
                scaleLabel: { display: true, labelString: "وضعیت دستگاه" }
              }
            ]
          },
          legend: { display: false },
          tooltips: {
            callbacks: {
              label: function(tooltipItem) {
                return tooltipItem.yLabel === 1 ? "آنلاین" : "آفلاین";
              }
            }
          }
        }
      });
    },

    // دقیقاً همون کد اصلی شما — بدون تغییر
    renderLogsChart() {
      const ctx = document.getElementById("device-logs-chart");
      if (this.logsChart) this.logsChart.destroy();
      if (!this.deviceLogs.length || !this.selectedLogField) return;

      const field = this.selectedLogField;
      const fieldInfo = this.plottableFields.find(f => f.value === field);
      const isBool = fieldInfo.type === "boolean";

      const data = this.deviceLogs
        .map(log => {
          const raw = log[field];
          if (raw == null || raw === "") return null;
          const y = isBool
            ? raw === true || raw === "true"
              ? 1
              : 0
            : parseFloat(raw);
          return { x: new Date(log.createdAt), y };
        })
        .filter(d => d !== null);

      if (data.length === 0) return;

      this.logsChart = new Chart(ctx, {
        type: "line",
        data: {
          datasets: [
            {
              label: this.formatFieldName(field),
              data: data,
              borderColor: "#2196f3",
              backgroundColor: "rgba(33,150,243,0.1)",
              fill: true,
              tension: 0,
              steppedLine: true,
              pointRadius: 3
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                type: "time",
                time: {
                  unit: "day",
                  tooltipFormat: "YYYY/MM/DD HH:mm:ss",
                  displayFormats: { day: "YYYY/MM/DD", hour: "HH:mm" }
                },
                scaleLabel: { display: true, labelString: "زمان" }
              }
            ],
            yAxes: [
              {
                ticks: isBool
                  ? {
                      callback: v =>
                        v === 1
                          ? this.booleanFieldMap[field].true
                          : this.booleanFieldMap[field].false,
                      stepSize: 1,
                      min: 0,
                      max: 1
                    }
                  : { beginAtZero: true },
                scaleLabel: {
                  display: true,
                  labelString: isBool
                    ? `${this.booleanFieldMap[field].true} / ${this.booleanFieldMap[field].false}`
                    : "مقدار"
                }
              }
            ]
          },
          legend: { display: false },
          tooltips: {
            callbacks: {
              label: function(tooltipItem) {
                const field = this.selectedLogField;
                const isBool =
                  this.plottableFields.find(f => f.value === field)?.type ===
                  "boolean";

                if (isBool) {
                  const map = this.booleanFieldMap[field];
                  return `${this.formatFieldName(field)}: ${
                    tooltipItem.yLabel === 1 ? map.true : map.false
                  }`;
                }
                return `${this.formatFieldName(field)}: ${tooltipItem.yLabel}`;
              }.bind(this)
            }
          }
        }
      });
    },

    resetLogSelection() {
      this.selectedLogField = null;
    },

    formatJalaliDate(datetime) {
      if (!datetime) return "-";
      try {
        return moment(datetime).format("jYYYY/jMM/jDD HH:mm:ss");
      } catch {
        return "-";
      }
    }
  }
};
</script>

<style scoped>
.IranSans {
  font-family: "IranSans", sans-serif !important;
}

.pa-6 {
  padding: 20px !important;
  background: #f9fbfd;
  min-height: 100vh;
}

.bread-row {
  margin-bottom: 8px !important;
  margin-top: -8px !important;
}

.calendar-row {
  margin-top: 8px !important;
  margin-bottom: 24px;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.calendar-wrapper {
  display: flex;
  flex-direction: column;
}

.calendar-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.chart-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.chart-container {
  position: relative;
  height: 260px;
  margin-top: 12px;
}

canvas {
  width: 100% !important;
  height: 100% !important;
}

.v-card {
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06) !important;
  transition: all 0.2s ease;
}
.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.v-card-title {
  font-weight: 600;
  font-size: 17px !important;
  color: #1a1a1a;
}

.title-font {
  font-family: "IranSans", sans-serif !important;
  font-weight: 600 !important;
}

.v-data-table {
  font-size: 13.5px;
  border-radius: 12px;
  overflow: hidden;
}

.v-chip {
  font-size: 12px;
  font-weight: 500;
}

.field-select {
  max-width: 320px;
}

.v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600;
}

@media (max-width: 960px) {
  .calendar-row {
    gap: 12px;
  }
  .chart-container {
    height: 300px;
  }
}
@media (max-width: 600px) {
  .pa-6 {
    padding: 12px !important;
  }
  .chart-container {
    height: 340px;
  }
  .v-card-title {
    font-size: 16px !important;
  }
}
</style>
